// Turnos de la planta (T1, T2, T3 — pueden cruzar medianoche).
// Mirror de la tabla `turnos` (migración 00003).
//
// `hora_inicio` y `hora_fin` son TIME (HH:MM:SS o HH:MM). Si `cruza_medianoche
// === true`, hora_fin < hora_inicio en el clock pero el turno corre del día
// anterior al actual (caso T3 22:00 → 06:00).
import { z } from 'zod';
const horaTimeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9](:[0-5][0-9])?$/;
export const turnoSchema = z.object({
    id: z.string().uuid(),
    tenant_id: z.string().uuid(),
    planta_id: z.string().uuid(),
    numero: z.number().int().min(1).max(3),
    nombre: z.string().trim().min(1).max(40),
    hora_inicio: z.string().regex(horaTimeRegex, 'Formato HH:MM o HH:MM:SS'),
    hora_fin: z.string().regex(horaTimeRegex, 'Formato HH:MM o HH:MM:SS'),
    cruza_medianoche: z.boolean(),
});
function toMin(h) {
    const [hh, mm] = h.split(':');
    return Number(hh) * 60 + Number(mm);
}
function isoDateFromUtcDate(d) {
    return d.toISOString().slice(0, 10);
}
function addDaysISO(fechaISO, plusDias) {
    const [y, m, d] = fechaISO.split('-').map(Number);
    if (!y || !m || !d)
        return fechaISO;
    return isoDateFromUtcDate(new Date(Date.UTC(y, m - 1, d + plusDias)));
}
/**
 * ¿La hora `now` (HH:MM) cae dentro de este turno?
 * Maneja `cruza_medianoche`: si T3 = 22:00 → 06:00, las 23:00 y las 03:00
 * están ambas dentro.
 */
export function isHoraInsideTurno(hora, turno) {
    const ini = toMin(turno.hora_inicio);
    const fin = toMin(turno.hora_fin);
    const cur = toMin(hora);
    if (!turno.cruza_medianoche)
        return cur >= ini && cur < fin;
    // Cruza medianoche: matchea si está entre ini..23:59 O entre 00:00..fin
    return cur >= ini || cur < fin;
}
/**
 * Ventana de gracia (minutos) tras `hora_fin` durante la cual el líder sigue
 * considerado "en turno" para capturar el cierre. Refleja la realidad del piso:
 * el último bloque (ej. 13:00–14:00 en T1) recién se termina a las 14:00 y se
 * graba minutos después. Igual valor que VENTANA_GRACIA_EDICION_MIN del API
 * (ADR-0004) — mantener en sync.
 */
export const VENTANA_GRACIA_CAPTURA_MIN = 60;
/**
 * ¿La hora `now` (HH:MM) cae dentro de la VENTANA DE CAPTURA del líder, es decir
 * `[hora_inicio, hora_fin + graciaMin)`? Extiende `isHoraInsideTurno` con la
 * gracia post-cierre. Maneja `cruza_medianoche` (T3 22:00→06:00: la ventana con
 * gracia llega hasta 07:00 del día siguiente).
 *
 * Sirve SOLO para la ventana de captura del líder — NO para "turno corriendo
 * ahora" (dashboards), que debe seguir usando `isHoraInsideTurno` (estricto).
 */
export function isHoraEnVentanaCaptura(hora, turno, graciaMin = VENTANA_GRACIA_CAPTURA_MIN) {
    const ini = toMin(turno.hora_inicio);
    // Si cruza medianoche, el fin cae al día siguiente → +24h para linealizar.
    let fin = toMin(turno.hora_fin);
    if (turno.cruza_medianoche && fin <= ini)
        fin += 24 * 60;
    const finConGracia = fin + graciaMin;
    let cur = toMin(hora);
    // Llevar `cur` al mismo eje lineal: si la ventana se extiende al día siguiente
    // (cruza medianoche, o fin+gracia pasa de medianoche) y `cur` es de madrugada
    // (< ini), corresponde a la cola del día siguiente.
    if (cur < ini && finConGracia > 24 * 60)
        cur += 24 * 60;
    return cur >= ini && cur < finConGracia;
}
/**
 * Primer día de la semana operativa que contiene `fechaISO`.
 * `diaInicioSemana`: ISO 1=lunes ... 7=domingo. Con 1 equivale a lunes ISO.
 */
export function inicioSemanaOperativa(fechaISO, diaInicioSemana = 1) {
    const [y, m, d] = fechaISO.split('-').map(Number);
    if (!y || !m || !d)
        return fechaISO;
    const utc = new Date(Date.UTC(y, m - 1, d));
    const jsDay = utc.getUTCDay(); // 0=domingo ... 6=sábado
    const isoDow = jsDay === 0 ? 7 : jsDay;
    const diff = (isoDow - diaInicioSemana + 7) % 7;
    return isoDateFromUtcDate(new Date(Date.UTC(y, m - 1, d - diff)));
}
function isoWeekKey(fechaISO) {
    const [y, m, d] = fechaISO.split('-').map(Number);
    if (!y || !m || !d)
        return '0000-W00';
    const date = new Date(Date.UTC(y, m - 1, d));
    const day = date.getUTCDay() || 7;
    date.setUTCDate(date.getUTCDate() + 4 - day);
    const isoYear = date.getUTCFullYear();
    const yearStart = new Date(Date.UTC(isoYear, 0, 1));
    const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86_400_000 + 1) / 7);
    return `${isoYear}-W${String(week).padStart(2, '0')}`;
}
/**
 * Clave IYYY-Wxx de la semana operativa, calculada como la semana ISO del día
 * central (`inicio + 3`). Evita ambigüedades en semanas domingo→sábado.
 */
export function semanaOperativaKeyDe(fechaISO, diaInicioSemana = 1) {
    return isoWeekKey(addDaysISO(inicioSemanaOperativa(fechaISO, diaInicioSemana), 3));
}
/**
 * Orden de días ISO para pintar una semana operativa.
 * Ej: lunes=1 → [1,2,3,4,5,6,7]; domingo=7 → [7,1,2,3,4,5,6].
 */
export function diasSemanaOrdenados(diaInicioSemana = 1) {
    return Array.from({ length: 7 }, (_, i) => ((diaInicioSemana - 1 + i) % 7) + 1);
}
/**
 * Inverso de `semanaOperativaKeyDe`: primer día de la semana operativa cuya
 * clave es `key`. El inicio es el único día con ISODOW=diaInicioSemana a
 * distancia ≤3 del día central de la clave (lunes ISO + 3): con lunes se
 * reduce al lunes ISO estándar; con domingo es el domingo ANTERIOR al lunes.
 */
export function inicioSemanaOperativaDeKey(key, diaInicioSemana = 1) {
    const m = key.match(/^(\d{4})-W(\d{2})$/);
    if (!m)
        return key;
    const anio = Number(m[1]);
    const semana = Number(m[2]);
    // Lunes ISO de la semana `key` (la semana 1 contiene el 4 de enero).
    const ene4 = new Date(Date.UTC(anio, 0, 4));
    const dowEne4 = ene4.getUTCDay() || 7;
    const lunesMs = Date.UTC(anio, 0, 4 - (dowEne4 - 1)) + (semana - 1) * 7 * 86_400_000;
    const offset = diaInicioSemana <= 4 ? diaInicioSemana - 1 : diaInicioSemana - 8;
    return isoDateFromUtcDate(new Date(lunesMs + offset * 86_400_000));
}
/**
 * Día calendario físico de un bloque/parada dentro de un turno.
 */
export function fechaFisicaBloque(fechaOperativaISO, horaHHMM, turno, atribucionCruce = 'dia_inicio') {
    if (!turno.cruza_medianoche)
        return fechaOperativaISO;
    const hora = horaHHMM.slice(0, 5);
    const inicio = turno.hora_inicio.slice(0, 5);
    if (atribucionCruce === 'dia_fin' && hora >= inicio)
        return addDaysISO(fechaOperativaISO, -1);
    if (atribucionCruce === 'dia_inicio' && hora < inicio)
        return addDaysISO(fechaOperativaISO, 1);
    return fechaOperativaISO;
}
/**
 * Compone fecha (YYYY-MM-DD) + hora (HH:MM) como ISO en "fake UTC" (la hora
 * local del tenant etiquetada Z — convención de almacenamiento de paros,
 * ver combinarFechaHora del FE). `plusDias` corre la fecha N días.
 */
function composeFakeUtc(fechaISO, horaHHMM, plusDias = 0) {
    const [y, m, d] = fechaISO.split('-').map(Number);
    const [hh, mm] = horaHHMM.slice(0, 5).split(':').map(Number);
    if (!y || !m || !d || hh === undefined || mm === undefined) {
        // Defensivo: epoch — el backend rechaza con check_violation.
        return new Date(0).toISOString();
    }
    return new Date(Date.UTC(y, m - 1, d + plusDias, hh, mm, 0)).toISOString();
}
/**
 * Timestamp físico fake-UTC de entrada del turno.
 */
export function inicioFisicoTurno(fechaOperativaISO, turno, atribucionCruce = 'dia_inicio') {
    const fechaFisica = turno.cruza_medianoche && atribucionCruce === 'dia_fin'
        ? addDaysISO(fechaOperativaISO, -1)
        : fechaOperativaISO;
    return composeFakeUtc(fechaFisica, turno.hora_inicio);
}
/**
 * Timestamp de un paro DENTRO de un turno, en fake UTC. `fechaISO` es la
 * fecha OPERATIVA del turno (el día en que arrancó). Para un turno
 * cruza_medianoche, las horas de la cola post-medianoche (HH:MM <
 * hora_inicio del turno) pertenecen al día calendario SIGUIENTE → +1 día.
 *
 * Esto hace monotónicos los timestamps a lo largo del turno (22:00 < 23:59 <
 * 00:10+1d < 05:59+1d) — sin esto, un paro 23:50→00:10 componía fin < inicio
 * y era imposible de guardar (CHECK paro_hora_orden).
 */
export function combinarFechaHoraTurno(fechaISO, horaHHMM, turno, atribucionCruce = 'dia_inicio') {
    return composeFakeUtc(fechaFisicaBloque(fechaISO, horaHHMM, turno, atribucionCruce), horaHHMM);
}
/**
 * Timestamp del CIERRE de un paro abierto, en fake UTC. Compone con la fecha
 * operativa; si el resultado queda ESTRICTAMENTE antes del inicio del paro,
 * el cierre cayó del otro lado de la medianoche → +1 día. (Si queda IGUAL al
 * inicio, se devuelve tal cual y la validación fin > inicio lo rechaza con
 * mensaje claro — un cierre en el mismo minuto del inicio no es un paro.)
 */
export function combinarHoraCierreParo(fechaISO, horaHHMM, horaInicioParoISO, turno, atribucionCruce = 'dia_inicio') {
    const directo = turno
        ? combinarFechaHoraTurno(fechaISO, horaHHMM, turno, atribucionCruce)
        : composeFakeUtc(fechaISO, horaHHMM);
    if (Date.parse(directo) < Date.parse(horaInicioParoISO)) {
        return composeFakeUtc(fechaISO, horaHHMM, 1);
    }
    return directo;
}
/**
 * Formatea minutos totales (ya lineales, puede exceder 24h) como "HH:MM" del
 * reloj (módulo 24h). `1440` → "00:00" (fin de día, convención compartida con
 * `hora_fin_bloque='00:00'` del resto del módulo).
 */
function formatHoraFromMin(totalMin) {
    const mm = totalMin % (24 * 60);
    const hh = Math.floor(mm / 60);
    const min = mm % 60;
    return `${String(hh).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}
/**
 * Avanza una hora "HH:MM" en `elapsedMs` milisegundos (módulo 24h). Elapsed
 * negativo se trata como 0 (reloj nunca retrocede).
 *
 * Uso: mantener vivo `hora_servidor` en el FE sin re-fetch — la hora base
 * viene del servidor (tz del tenant) y acá solo se le suma el tiempo
 * TRANSCURRIDO en el dispositivo (delta monotónico, inmune a que el reloj
 * del celular esté en otra zona o desajustado en absoluto). Sin esto, una
 * PWA que queda abierta captura bloques con la hora del último fetch
 * (incidente T3 Vertical 1 del 22-jul-2026: bloque de 01:00 guardado como
 * un segundo 00:00).
 */
export function avanzarHoraHHMM(horaHHMM, elapsedMs) {
    const mins = toMin(horaHHMM) + Math.floor(Math.max(0, elapsedMs) / 60_000);
    return formatHoraFromMin(mins);
}
/**
 * Bloques horarios de un turno como rangos `BloqueTurno[]` (BREAKING: antes
 * `string[]`). Un turno 06:00→14:00 da 8 bloques de 60' idénticos a la
 * versión anterior — no-regresión. Maneja `cruza_medianoche`: 22:00→06:00 da
 * bloques 22:00, 23:00, 00:00, …, 05:00, todos de 60'.
 *
 * **Clipping de bordes**: si `hora_inicio`/`hora_fin` no caen en `:00` (turno
 * con borde parcial, ej. Sopó T2 13:30→21:00), el PRIMER y/o ÚLTIMO bloque se
 * recorta contra el borde real del turno. Los bloques intermedios SIEMPRE
 * arrancan en `:00` y duran 60' — por construcción NUNCA hay parciales
 * intermedios (INV-1), no hace falta validarlo en runtime.
 *
 * Esta función ES hoy la única estrategia de derivación de bloques
 * (`derivarBloques` conceptual). Un futuro modo `plantilla` (bloques
 * definidos manualmente por el coordinador) alimentaría la MISMA lista
 * `BloqueTurno[]` sin tocar los consumidores (captura FE, back-fill BE, P1) —
 * punto de extensión documentado, NO implementado (YAGNI confirmado).
 *
 * Fuente única de verdad compartida FE↔BE: el FE la usa para detectar bloques
 * saltados y el BE para validar el back-fill del líder.
 */
export function generarBloquesTurno(turno) {
    const ini = toMin(turno.hora_inicio);
    // Cruza medianoche: el fin cae al día siguiente → sumamos 24h para iterar.
    const fin = turno.cruza_medianoche ? toMin(turno.hora_fin) + 24 * 60 : toMin(turno.hora_fin);
    const bloques = [];
    let cur = ini;
    while (cur < fin) {
        const proximoBorde = (Math.floor(cur / 60) + 1) * 60; // próximo ":00" del reloj
        const bloqueFin = Math.min(proximoBorde, fin); // recorta contra el fin del turno
        bloques.push({
            inicio: formatHoraFromMin(cur),
            fin: formatHoraFromMin(bloqueFin),
            duracionMinutos: bloqueFin - cur,
        });
        cur = bloqueFin;
    }
    return bloques;
}
/**
 * ¿Qué bloque de `bloques` (generados por `generarBloquesTurno`) contiene
 * `horaServidor` (HH:MM, hora local del tenant)? Contención por RANGOS,
 * JAMÁS por `HH:00`/`date_trunc('hour')`: 13:35 en un turno con bloque
 * 13:30–14:00 devuelve ESE bloque, no uno de 13:00–14:00.
 *
 * Semántica `[inicio, fin)`: el borde exacto (ej. 14:00 en punto) pertenece
 * al bloque SIGUIENTE — coherente con el EXCLUDE gist `tstzrange` de la BD.
 *
 * Linealiza acumulando +1440 minutos cada vez que el `inicio` de un bloque es
 * menor que el del bloque previo (cruce de medianoche); usa `duracionMinutos`
 * del contrato para `absFin` (evita el wrap de `fin="00:00"` → 0). Si
 * `horaServidor` cae antes del primer bloque, se asume cola post-medianoche
 * (+1440) antes de comparar.
 *
 * Devuelve `null` si `horaServidor` no cae dentro de ningún bloque (fuera de
 * turno).
 */
export function bloqueQueContieneHora(bloques, horaServidor) {
    if (bloques.length === 0)
        return null;
    let cur = toMin(horaServidor);
    const primerIni = toMin(bloques[0].inicio);
    if (cur < primerIni)
        cur += 24 * 60; // cola post-medianoche
    let offset = 0;
    let prevIni = -1;
    for (const b of bloques) {
        const ini = toMin(b.inicio);
        if (ini < prevIni)
            offset += 24 * 60; // cruzó medianoche entre este bloque y el previo
        const absIni = ini + offset;
        const absFin = absIni + b.duracionMinutos;
        if (cur >= absIni && cur < absFin)
            return b;
        prevIni = ini;
    }
    return null;
}
//# sourceMappingURL=turno.js.map